import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Tab,
  Tabs,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Slider,
  FormControlLabel,
  Switch,
  Divider,
  Card,
  CardContent,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Code as CodeIcon,
  Send as SendIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  GroupWork as MultiAgentIcon,
  Architecture as ArchitectureIcon,
  Speed as OptimizeIcon,
  Description as DocsIcon
} from '@mui/icons-material';
import { codingService } from '../services/codingService';
import { CodingSession, CodingIteration } from '../models/coding';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`coding-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

const CodingAgent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Single Agent State
  const [purpose, setPurpose] = useState('');
  const [codeFile, setCodeFile] = useState('');
  const [iterations, setIterations] = useState(3);
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2000);
  const [currentSession, setCurrentSession] = useState<CodingSession | null>(null);
  const [feedback, setFeedback] = useState('');
  const [sessionMenuAnchor, setSessionMenuAnchor] = useState<null | HTMLElement>(null);
  const [savedSessions, setSavedSessions] = useState<string[]>([]);
  
  // Multi-Agent State
  const [multiAgentMode, setMultiAgentMode] = useState(false);
  const [description, setDescription] = useState('');
  const [codeContext, setCodeContext] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('');
  const [multiAgentResponse, setMultiAgentResponse] = useState<any>(null);
  const [selectedAgentOutput, setSelectedAgentOutput] = useState<'architecture' | 'implementation' | 'review' | 'optimization' | 'documentation' | 'tests' | 'deployment' | 'final'>('final');

  useEffect(() => {
    loadSavedSessions();
  }, []);

  const loadSavedSessions = () => {
    const sessionIds = codingService.getAllSessionIds();
    setSavedSessions(sessionIds);
  };

  const handleStartSession = async () => {
    if (!purpose.trim()) {
      setError('Please provide a purpose for the code');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      if (multiAgentMode) {
        const response = await codingService.startMultiAgentSession({
          description: purpose,
          codeContext: codeContext || undefined,
          targetLanguage: targetLanguage || undefined,
          maxResponseTokens: maxTokens,
          temperature
        });
        
        setMultiAgentResponse(response);
        
        // Save to local storage
        codingService.saveSessionToLocalStorage(response.sessionId, {
          type: 'multi-agent',
          description: purpose,
          response,
          createdAt: new Date().toISOString()
        });
      } else {
        const response = await codingService.startSession({
          purpose,
          codeFile: codeFile || undefined,
          iterations,
          maxTokensPerIteration: maxTokens,
          temperature
        });
        
        const newSession: CodingSession = {
          id: response.sessionId,
          createdAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString(),
          purpose,
          codeFile: codeFile || undefined,
          iterations: response.codeIterations?.map((code, index) => ({
            iterationNumber: index + 1,
            code,
            timestamp: new Date().toISOString()
          })) || [],
          isActive: true
        };
        
        setCurrentSession(newSession);
        
        // Save to local storage
        codingService.saveSessionToLocalStorage(response.sessionId, newSession);
      }
      
      loadSavedSessions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start coding session');
    } finally {
      setLoading(false);
    }
  };

  const handleIterate = async () => {
    if (!currentSession || !feedback.trim()) {
      setError('Please provide feedback for the iteration');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await codingService.iterate(currentSession.id, {
        feedback,
        maxTokensPerIteration: maxTokens,
        temperature
      });
      
      const newIteration: CodingIteration = {
        iterationNumber: response.iterationNumber || currentSession.iterations.length + 1,
        code: response.code || '',
        explanation: response.explanation,
        feedback,
        timestamp: new Date().toISOString()
      };
      
      const updatedSession = {
        ...currentSession,
        iterations: [...currentSession.iterations, newIteration],
        lastUpdated: new Date().toISOString()
      };
      
      setCurrentSession(updatedSession);
      setFeedback('');
      
      // Update local storage
      codingService.saveSessionToLocalStorage(currentSession.id, updatedSession);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to iterate on code');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSession = (sessionId: string) => {
    const sessionData = codingService.getSessionFromLocalStorage(sessionId);
    if (sessionData) {
      if (sessionData.type === 'multi-agent') {
        setMultiAgentMode(true);
        setMultiAgentResponse(sessionData.response);
        setPurpose(sessionData.description);
      } else {
        setMultiAgentMode(false);
        setCurrentSession(sessionData);
        setPurpose(sessionData.purpose);
        setCodeFile(sessionData.codeFile || '');
      }
    }
    setSessionMenuAnchor(null);
  };

  const handleDeleteSession = (sessionId: string) => {
    codingService.deleteSessionFromLocalStorage(sessionId);
    loadSavedSessions();
    if (currentSession?.id === sessionId) {
      setCurrentSession(null);
    }
    if (multiAgentResponse?.sessionId === sessionId) {
      setMultiAgentResponse(null);
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  const handleDownloadCode = (code: string, filename: string) => {
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getAgentOutputContent = () => {
    if (!multiAgentResponse) return '';
    
    switch (selectedAgentOutput) {
      case 'architecture': return multiAgentResponse.architecture || '';
      case 'implementation': return multiAgentResponse.implementation || '';
      case 'review': return multiAgentResponse.review || '';
      case 'optimization': return multiAgentResponse.optimization || '';
      case 'documentation': return multiAgentResponse.documentation || '';
      case 'tests': return multiAgentResponse.tests || '';
      case 'deployment': return multiAgentResponse.deployment || '';
      case 'final': return multiAgentResponse.finalCode || '';
      default: return '';
    }
  };

  const agentOutputTabs = [
    { id: 'final', label: 'Final Code', icon: <CodeIcon /> },
    { id: 'architecture', label: 'Architecture', icon: <ArchitectureIcon /> },
    { id: 'implementation', label: 'Implementation', icon: <CodeIcon /> },
    { id: 'review', label: 'Code Review', icon: <CodeIcon /> },
    { id: 'optimization', label: 'Optimization', icon: <OptimizeIcon /> },
    { id: 'documentation', label: 'Documentation', icon: <DocsIcon /> },
    { id: 'tests', label: 'Tests', icon: <CodeIcon /> },
    { id: 'deployment', label: 'Deployment', icon: <CodeIcon /> }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' } }}
        >
          AI Coding Assistant
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          paragraph
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {isMobile ? 'Generate & iterate on code with AI assistance' : 'Generate, improve, and iterate on code with AI assistance. Use single-agent mode for iterative development or multi-agent mode for comprehensive code generation.'}
        </Typography>

        <Stack 
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2 }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: { xs: 2, sm: 3 } }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={multiAgentMode}
                onChange={(e) => setMultiAgentMode(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <MultiAgentIcon fontSize={isMobile ? 'small' : 'medium'} />
                <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Multi-Agent Mode
                </Typography>
              </Stack>
            }
          />
          
          <IconButton 
            onClick={(e) => setSessionMenuAnchor(e.currentTarget)}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <Badge badgeContent={savedSessions.length} color="primary">
              <HistoryIcon fontSize={isMobile ? 'small' : 'medium'} />
            </Badge>
          </IconButton>
          
          <Menu
            anchorEl={sessionMenuAnchor}
            open={Boolean(sessionMenuAnchor)}
            onClose={() => setSessionMenuAnchor(null)}
            PaperProps={{
              sx: {
                maxWidth: { xs: '90vw', sm: 400 }
              }
            }}
          >
            {savedSessions.length === 0 ? (
              <MenuItem disabled>
                <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  No saved sessions
                </Typography>
              </MenuItem>
            ) : (
              savedSessions.map((sessionId) => {
                const session = codingService.getSessionFromLocalStorage(sessionId);
                return (
                  <MenuItem key={sessionId}>
                    <Stack 
                      direction="row" 
                      spacing={{ xs: 1, sm: 2 }}
                      alignItems="center" 
                      sx={{ width: '100%' }}
                    >
                      <Box 
                        sx={{ flexGrow: 1, minWidth: 0 }} 
                        onClick={() => handleLoadSession(sessionId)}
                      >
                        <Typography 
                          variant="body2" 
                          noWrap
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          {session.purpose || session.description || 'Untitled Session'}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                          {new Date(session.createdAt || session.lastUpdated).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSession(sessionId);
                        }}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </MenuItem>
                );
              })
            )}
          </Menu>
        </Stack>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: { xs: 1, sm: 2 },
              fontSize: { xs: '0.8125rem', sm: '0.875rem' }
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Input Section */}
        {!currentSession && !multiAgentResponse && (
          <Box>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 3 : 4}
              label={multiAgentMode ? (isMobile ? "Describe what to build" : "Describe what you want to build") : "What code do you need?"}
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder={multiAgentMode 
                ? (isMobile ? "Build a REST API..." : "Build a REST API for a todo list application with authentication...")
                : (isMobile ? "Create a function..." : "Create a function that sorts an array of objects by date...")
              }
              sx={{ mb: { xs: 1, sm: 2 } }}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            {!multiAgentMode && (
              <TextField
                fullWidth
                label="Code File (optional)"
                value={codeFile}
                onChange={(e) => setCodeFile(e.target.value)}
                placeholder="e.g., utils.js, api.py"
                sx={{ mb: { xs: 1, sm: 2 } }}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
            )}

            {multiAgentMode && (
              <>
                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 2 : 3}
                  label="Code Context (optional)"
                  value={codeContext}
                  onChange={(e) => setCodeContext(e.target.value)}
                  placeholder={isMobile ? "Provide context..." : "Provide any existing code or context..."}
                  sx={{ mb: { xs: 1, sm: 2 } }}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
                <TextField
                  fullWidth
                  label="Target Language (optional)"
                  value={targetLanguage}
                  onChange={(e) => setTargetLanguage(e.target.value)}
                  placeholder="e.g., Python, JavaScript, Java"
                  sx={{ mb: { xs: 1, sm: 2 } }}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
              </>
            )}

            <Box sx={{ mb: { xs: 1, sm: 2 } }}>
              <Typography 
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                Temperature: {temperature}
              </Typography>
              <Slider
                value={temperature}
                onChange={(_, value) => setTemperature(value as number)}
                min={0}
                max={1}
                step={0.1}
                marks
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-markLabel': {
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                  }
                }}
              />
            </Box>

            <Box sx={{ mb: { xs: 1, sm: 2 } }}>
              <Typography 
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                Max Tokens: {maxTokens}
              </Typography>
              <Slider
                value={maxTokens}
                onChange={(_, value) => setMaxTokens(value as number)}
                min={500}
                max={4000}
                step={100}
                marks
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-markLabel': {
                    fontSize: { xs: '0.65rem', sm: '0.75rem' }
                  }
                }}
              />
            </Box>

            {!multiAgentMode && (
              <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                <Typography 
                  gutterBottom
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  Iterations: {iterations}
                </Typography>
                <Slider
                  value={iterations}
                  onChange={(_, value) => setIterations(value as number)}
                  min={1}
                  max={5}
                  step={1}
                  marks
                  valueLabelDisplay="auto"
                  sx={{
                    '& .MuiSlider-markLabel': {
                      fontSize: { xs: '0.7rem', sm: '0.75rem' }
                    }
                  }}
                />
              </Box>
            )}

            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleStartSession}
              disabled={loading || !purpose.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <SendIcon />)}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 44 
              }}
            >
              {loading ? 'Generating...' : 'Generate Code'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Single Agent Results */}
      {currentSession && !multiAgentMode && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
          >
            Coding Session: {currentSession.purpose}
          </Typography>
          
          <Tabs 
            value={tabValue} 
            onChange={(_, v) => setTabValue(v)} 
            sx={{ 
              mb: { xs: 1, sm: 2 },
              '& .MuiTab-root': {
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                minHeight: { xs: 44, sm: 48 }
              }
            }}
            variant={isMobile ? 'scrollable' : 'standard'}
            {...(isMobile && { scrollButtons: 'auto' })}
          >
            {currentSession.iterations.map((_, index) => (
              <Tab key={index} label={`Iteration ${index + 1}`} />
            ))}
          </Tabs>

          {currentSession.iterations.map((iteration, index) => (
            <TabPanel key={index} value={tabValue} index={index}>
              <Card sx={{ mb: { xs: 1, sm: 2 } }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                  {iteration.feedback && (
                    <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        Feedback:
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                      >
                        {iteration.feedback}
                      </Typography>
                    </Box>
                  )}
                  
                  {iteration.explanation && (
                    <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        Explanation:
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                      >
                        {iteration.explanation}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                    <Stack 
                      direction="row" 
                      justifyContent="space-between" 
                      alignItems="center" 
                      sx={{ mb: 1 }}
                    >
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        Code:
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Copy code">
                          <IconButton 
                            size="small" 
                            onClick={() => handleCopyCode(iteration.code)}
                            sx={{ minWidth: 44, minHeight: 44 }}
                          >
                            <CopyIcon fontSize={isMobile ? 'small' : 'medium'} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download code">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDownloadCode(
                              iteration.code, 
                              `${currentSession.codeFile || 'code'}_iteration_${index + 1}.txt`
                            )}
                            sx={{ minWidth: 44, minHeight: 44 }}
                          >
                            <DownloadIcon fontSize={isMobile ? 'small' : 'medium'} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                    <Box sx={{ maxHeight: { xs: 300, sm: 400 }, overflow: 'auto' }}>
                      <SyntaxHighlighter
                        language="javascript"
                        style={vscDarkPlus}
                        customStyle={{ 
                          margin: 0,
                          fontSize: isMobile ? '0.75rem' : '0.875rem'
                        }}
                      >
                        {iteration.code}
                      </SyntaxHighlighter>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </TabPanel>
          ))}

          {currentSession.isActive && (
            <Box sx={{ mt: { xs: 2, sm: 3 } }}>
              <Divider sx={{ mb: { xs: 2, sm: 3 } }} />
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
              >
                Continue Iterating
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 2 : 3}
                label="Provide feedback for the next iteration"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={isMobile ? "Add feedback..." : "The function works but could be more efficient. Can you optimize the sorting algorithm?"}
                sx={{ mb: { xs: 1, sm: 2 } }}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
              <Button
                variant="contained"
                fullWidth
                onClick={handleIterate}
                disabled={loading || !feedback.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <SendIcon />)}
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minHeight: 44
                }}
              >
                {loading ? 'Iterating...' : 'Iterate on Code'}
              </Button>
            </Box>
          )}

          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            <Button
              variant="outlined"
              onClick={() => {
                setCurrentSession(null);
                setPurpose('');
                setCodeFile('');
                setFeedback('');
                setTabValue(0);
              }}
              startIcon={!isMobile && <ClearIcon />}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 44,
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Start New Session
            </Button>
          </Box>
        </Paper>
      )}

      {/* Multi-Agent Results */}
      {multiAgentResponse && multiAgentMode && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
          >
            Multi-Agent Code Generation Results
          </Typography>
          
          {multiAgentResponse.summary && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '0.8125rem', sm: '0.875rem' }
              }}
            >
              {multiAgentResponse.summary}
            </Alert>
          )}

          <Tabs 
            value={selectedAgentOutput} 
            onChange={(_, v) => setSelectedAgentOutput(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              mb: { xs: 1, sm: 2 },
              '& .MuiTab-root': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minHeight: { xs: 44, sm: 48 }
              }
            }}
          >
            {agentOutputTabs.map((tab) => (
              <Tab 
                key={tab.id} 
                value={tab.id} 
                label={tab.label} 
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>

          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
              <Stack 
                direction="row" 
                justifyContent="space-between" 
                alignItems="center" 
                sx={{ mb: { xs: 1, sm: 2 } }}
              >
                <Typography 
                  variant="h6"
                  sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                >
                  {agentOutputTabs.find(t => t.id === selectedAgentOutput)?.label}
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Copy code">
                    <IconButton 
                      onClick={() => handleCopyCode(getAgentOutputContent())}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <CopyIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download code">
                    <IconButton 
                      onClick={() => handleDownloadCode(
                        getAgentOutputContent(), 
                        `${selectedAgentOutput}_code.txt`
                      )}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <DownloadIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              
              <Box sx={{ maxHeight: { xs: 400, sm: 600 }, overflow: 'auto' }}>
                <SyntaxHighlighter
                  language={targetLanguage.toLowerCase() || 'javascript'}
                  style={vscDarkPlus}
                  customStyle={{ 
                    margin: 0,
                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                  }}
                >
                  {getAgentOutputContent() || 'No content generated for this section'}
                </SyntaxHighlighter>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            <Button
              variant="outlined"
              onClick={() => {
                setMultiAgentResponse(null);
                setPurpose('');
                setCodeContext('');
                setTargetLanguage('');
              }}
              startIcon={!isMobile && <ClearIcon />}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 44,
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Start New Session
            </Button>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default CodingAgent;